import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Send, Clock, Phone, Car, RefreshCw } from 'lucide-react';
import MobileListScreen from './MobileListScreen';
import QuoteFormModal from '../QuoteFormModal';
import { MobileJobDetail } from '../MobileJobsDashboard';
import { fetchQuotes, sendQuoteForApproval, clearQuoteError } from '../../store/slices/quoteSlice';
import { fetchJob, fetchWorkCategories, fetchCategoryTypes, clearSelectedJob } from '../../store/slices/jobSlice';
import { useJobDetailActions } from '../../hooks/useJobDetailActions';
import { formatAud } from '../../utils/money';

/**
 * Quotes on a phone — raise one at the counter, chase the ones already sent.
 *
 * Two segments, the same split as the desktop board and from the same source of truth:
 * a quote is "sent" when the server says `customerApprovalPending`. Deriving it here
 * from a status string would be a second, weaker copy of a rule the front end does not
 * own, and the two screens would disagree the first time a status was added.
 *
 * Creating reuses <QuoteFormModal> full-screen, exactly as MobileBookingPage reuses
 * BookingFormModal. That keeps one customer/vehicle picker in the product — the one
 * with the rego-ownership warning and the create-new-customer path — rather than a
 * thinner phone copy that quietly accepts different things.
 *
 * Tapping a quote opens <MobileJobDetail> — the SAME full-screen detail the jobs
 * board opens, because a quote is a job. That is what makes pricing possible on a
 * phone: the work-item editor, its cost breakdown and the notes feed are already
 * there, and a quote-only copy of them would be a second editor posting to the same
 * API with different rules. Its footer swaps the lifecycle buttons for "Send for
 * approval" while the quote is unanswered.
 *
 * The phone-specific part is the CALL BUTTON. A quote sitting in "sent for approval" is
 * a customer who has not answered, and the action a service advisor takes standing in
 * the workshop is to ring them. On a desktop that number is something to read; on a
 * phone it is something to tap.
 */

const SEGMENTS = [
    { key: 'created', label: 'Created' },
    { key: 'sent', label: 'Sent' },
];

/** Both segments come from one flag — see the file comment. */
const isSent = (quote) => !!quote.customerApprovalPending;

const QuoteRow = ({ quote, onSend, onOpen, sending }) => {
    const hasWorkItems = (quote.workItemCount ?? 0) > 0;
    const sent = isSent(quote);

    return (
        <li className="border-b border-gray-100 px-4 py-3">
            <button type="button" onClick={() => onOpen(quote)}
                    className="w-full text-left">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-900">
                            {quote.customerName}
                        </p>
                        <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-gray-500">
                            <Car size={12} className="shrink-0 text-gray-400" />
                            {quote.vehicleDescription}
                        </p>
                    </div>
                    <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold text-gray-900">
                            {formatAud(quote.totalEstimatedCost)}
                        </p>
                        <p className="text-[11px] text-gray-400">
                            {hasWorkItems
                                ? `${quote.workItemCount} item${quote.workItemCount === 1 ? '' : 's'}`
                                : 'No items yet'}
                        </p>
                    </div>
                </div>
            </button>

            <div className="mt-2.5 flex items-center gap-2">
                {/* Tap to ring. A quote nobody has answered is a phone call waiting to
                    happen, and the number is already on the card. tel: is left to the
                    OS — it works on every phone and needs no permission. */}
                {quote.customerPhone && (
                    <a
                        href={`tel:${quote.customerPhone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl
                                   border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold
                                   text-emerald-700 active:bg-emerald-100"
                    >
                        <Phone size={13} /> Call
                    </a>
                )}

                {sent ? (
                    <span className="flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl
                                     border border-violet-200 bg-violet-50 px-3 text-xs font-medium text-violet-700">
                        <Clock size={13} /> Awaiting reply
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={() => onSend(quote)}
                        disabled={!hasWorkItems || sending}
                        // Said on the button rather than left to a failed request: the
                        // server refuses a quote with nothing priced on it.
                        title={hasWorkItems ? 'Send every priced item to the customer' : 'Add a priced work item first'}
                        className="flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl
                                   bg-violet-600 px-3 text-xs font-semibold text-white
                                   active:bg-violet-700 disabled:opacity-40"
                    >
                        <Send size={13} /> {sending ? 'Sending…' : 'Send'}
                    </button>
                )}
            </div>
        </li>
    );
};

const MobileQuotesPage = () => {
    const dispatch = useDispatch();
    const { quotes, loading, error, operationLoading, operationError } =
        useSelector((s) => s.quotes);
    // The open quote is the jobs slice's selectedJob: it IS a job, and reusing that
    // slice is what lets every work-item thunk update the open screen unchanged.
    const { selectedJob, workCategories, categoryTypes, operationLoading: jobOperationLoading } =
        useSelector((s) => s.jobs);

    const [segment, setSegment] = useState('created');
    const [showForm, setShowForm] = useState(false);
    const [sendingUuid, setSendingUuid] = useState(null);
    const [openUuid, setOpenUuid] = useState(null);

    const load = useCallback(() => dispatch(fetchQuotes()), [dispatch]);

    useEffect(() => { load(); }, [load]);

    // The work-item form needs these to populate its category pickers; without them
    // it renders empty dropdowns and nothing says why.
    useEffect(() => {
        dispatch(fetchWorkCategories());
        dispatch(fetchCategoryTypes());
    }, [dispatch]);

    const jobActions = useJobDetailActions(selectedJob);

    const handleOpen = (quote) => {
        setOpenUuid(quote.uuid);
        dispatch(fetchJob(quote.uuid));
    };

    const handleCloseDetail = () => {
        setOpenUuid(null);
        dispatch(clearSelectedJob());
        // Prices and item counts on the cards behind are stale the moment anything
        // was edited in there.
        load();
    };

    const handleSend = async (quote) => {
        setSendingUuid(quote.uuid);
        dispatch(clearQuoteError());
        try {
            await dispatch(sendQuoteForApproval(quote.uuid)).unwrap();
            // Sending from inside the detail screen closes it: the quote is now with
            // the customer and there is nothing further to do to it here.
            if (openUuid) {
                setOpenUuid(null);
                dispatch(clearSelectedJob());
            }
            load();
            // Follow the card: it has just moved to the other segment, and a list that
            // silently loses the row you acted on reads as a failure.
            setSegment('sent');
        } catch {
            // operationError renders the server's own sentence in the banner.
        } finally {
            setSendingUuid(null);
        }
    };

    const visible = quotes.filter((q) => (segment === 'sent' ? isSent(q) : !isSent(q)));

    return (
        <>
            <MobileListScreen
                title="Quotes"
                action={
                    <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        className="flex min-h-[40px] items-center gap-1.5 rounded-xl bg-violet-600 px-3
                                   text-sm font-semibold text-white active:bg-violet-700"
                    >
                        <Plus size={15} /> Quote
                    </button>
                }
                filters={[{
                    ariaLabel: 'Quote status',
                    value: segment,
                    onChange: setSegment,
                    // SegmentedControl's contract is { key, label, badge } — not
                    // value/count. Getting it wrong renders blank pills with no error.
                    options: SEGMENTS.map((s) => ({
                        key: s.key,
                        label: s.label,
                        badge: quotes.filter((q) => (s.key === 'sent' ? isSent(q) : !isSent(q))).length || null,
                    })),
                }]}
                loading={loading}
                error={error || operationError}
                onRefresh={load}
                items={visible}
                renderItem={(quote) => (
                    <QuoteRow
                        key={quote.uuid}
                        quote={quote}
                        onSend={handleSend}
                        onOpen={handleOpen}
                        sending={sendingUuid === quote.uuid && operationLoading}
                    />
                )}
                // ⚠️ An OBJECT, not a string — ListEmpty reads .title/.detail off it.
                // A string leaves the screen blank with no explanation.
                empty={segment === 'sent'
                    ? { icon: Clock, title: 'Nothing waiting on a customer' }
                    : { icon: Plus, title: 'No quotes yet', detail: 'Tap Quote to raise one.' }}
            />

            {/* Pricing a quote on the phone: the jobs board's own detail screen, with
                its footer swapped for Send while the customer has not answered. */}
            {openUuid && selectedJob?.uuid === openUuid && (
                <MobileJobDetail
                    job={selectedJob}
                    operationLoading={jobOperationLoading}
                    workCategories={workCategories}
                    categoryTypes={categoryTypes}
                    onClose={handleCloseDetail}
                    onSendQuote={handleSend}
                    onAddWorkItem={jobActions.addWorkItem}
                    onEditWorkItem={jobActions.editWorkItem}
                    onRemoveWorkItem={jobActions.removeWorkItem}
                    onAddNote={jobActions.addNote}
                    onRefreshJob={jobActions.refreshJob}
                    // Lifecycle actions cannot be reached from here: a pending quote
                    // shows none of them, and the moment it is answered it leaves this
                    // screen for the jobs board, which owns those buttons.
                    onStartJob={() => {}}
                    onPauseJob={() => {}}
                    onResumeJob={() => {}}
                    onOpenComplete={() => {}}
                />
            )}

            {openUuid && selectedJob?.uuid !== openUuid && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                        <RefreshCw size={28} className="animate-spin" />
                        <span className="text-sm">Loading quote…</span>
                    </div>
                </div>
            )}

            {showForm && (
                <QuoteFormModal
                    onClose={() => setShowForm(false)}
                    onCreated={() => {
                        setShowForm(false);
                        setSegment('created');
                        load();
                    }}
                />
            )}
        </>
    );
};

export default MobileQuotesPage;
